import { ParseError } from './Error';
export class BaseToken {
    constructor(value, position, type) {
        this.value = value;
        this.position = position;
        this.type = type;
    }
}
var Context;
(function (Context) {
    Context[Context["Expression"] = 0] = "Expression";
    Context[Context["ObjectLiteral"] = 1] = "ObjectLiteral";
    Context[Context["StringInterpolationExpression"] = 2] = "StringInterpolationExpression";
    Context[Context["StringInterpolationText"] = 3] = "StringInterpolationText";
})(Context || (Context = {}));
export class IdentifierToken extends BaseToken {
    constructor(value, position) {
        super(value, position, 'identifier');
        this.value = value;
    }
}
// longer length operators must come before shorter ones
export const operators = [
    '>>>=',
    '===',
    '!==',
    '>>>',
    '**=',
    '<<=',
    '>>=',
    '&&=',
    '||=',
    '??=',
    // '${' is handled separately
    '**',
    '<=',
    '>=',
    '==',
    '!=',
    '&=',
    '^=',
    '|=',
    '&&',
    '||',
    '++',
    '--',
    '<<',
    '>>',
    '??',
    '?.',
    '+=',
    '-=',
    '*=',
    '/=',
    '%=',
    '.',
    '{',
    '}',
    '(',
    ')',
    '[',
    ']',
    ',',
    ':',
    ';',
    "'",
    '"',
    '+',
    '-',
    '*',
    '/',
    '%',
    '^',
    '?',
    '<',
    '>',
    '!',
    '=',
    '&',
    '|',
    '`',
    '~',
];
export class OperatorToken extends BaseToken {
    constructor(value, position) {
        super(value, position, 'operator');
        this.value = value;
    }
}
export class Lexer {
    constructor(input) {
        this.input = input;
        this.contextStack = [Context.Expression];
        this.index = 0;
        this.tokens = [];
    }
    static lex(input) {
        const lexer = new Lexer(input);
        while (lexer.scanToken())
            ;
        return lexer.tokens;
    }
    get context() {
        return this.contextStack[this.contextStack.length - 1];
    }
    advance(count) {
        if (count === undefined) {
            const ch = this.input[this.index];
            this.index++;
            return ch;
        }
        const str = this.input.substring(this.index, this.index + count);
        this.index += str.length;
        return str.length === 0 ? undefined : str;
    }
    current(count = 1) {
        if (this.isEnd())
            return undefined;
        if (count === 1)
            return this.input[this.index];
        return this.input.substring(this.index, count);
    }
    isEnd() {
        return this.index >= this.input.length;
    }
    isInterpolationStart(relativePosition = 0) {
        return (this.input[this.index + relativePosition] === '$' &&
            this.input[this.index + 1 + relativePosition] === '{');
    }
    scanClosingBacktick() {
        const start = this.index;
        if (this.input[this.index] === '`') {
            this.index++;
            this.tokens.push(new OperatorToken('`', start));
            return true;
        }
        return false;
    }
    scanHexadecimal() {
        const start = this.index;
        const match = Lexer.hexadecimalRegExp.exec(this.input.substring(this.index));
        if (match) {
            const value = match[0];
            this.index += value.length;
            this.tokens.push(new ValueToken(parseInt(value, 16), start));
            return true;
        }
        return false;
    }
    scanIdentifier() {
        const start = this.index;
        const match = Lexer.identifierRegExp.exec(this.input.substring(this.index));
        if (match) {
            const value = match[0];
            this.index += value.length;
            if (value === 'null') {
                this.tokens.push(new ValueToken(null, start));
                return true;
            }
            else if (value === 'undefined') {
                this.tokens.push(new ValueToken(undefined, start));
                return true;
            }
            else if (value === 'true') {
                this.tokens.push(new ValueToken(true, start));
                return true;
            }
            else if (value === 'false') {
                this.tokens.push(new ValueToken(false, start));
                return true;
            }
            this.tokens.push(new IdentifierToken(value, start));
            return true;
        }
        return false;
    }
    scanInterpolationStart() {
        const start = this.index;
        const match = Lexer.interpolationStartRegExp.exec(this.input.substring(this.index));
        if (match) {
            const value = match[0];
            this.index += value.length;
            this.tokens.push(new OperatorToken(value, start));
            return true;
        }
        return false;
    }
    scanNumber() {
        const start = this.index;
        const match = Lexer.numberRegExp.exec(this.input.substring(this.index));
        if (match) {
            const value = match[0];
            this.index += value.length;
            this.tokens.push(new ValueToken(parseFloat(value), start));
            return true;
        }
        return false;
    }
    scanOperator() {
        const start = this.index;
        const remainder = this.input.substring(this.index);
        for (const operator of operators) {
            if (remainder.startsWith(operator)) {
                this.index += operator.length;
                switch (operator) {
                    case '{':
                        this.contextStack.push(Context.ObjectLiteral);
                        break;
                    case '}':
                        this.contextStack.pop();
                        break;
                    case '`':
                        this.contextStack.push(Context.StringInterpolationText);
                        break;
                    case '"':
                    case "'":
                        return this.scanString(operator, false);
                    default:
                        break;
                }
                this.tokens.push(new OperatorToken(operator, start));
                return true;
            }
        }
        return false;
    }
    scanString(quote, interpolate) {
        var _a, _b;
        const start = this.index;
        const buf = [];
        let ch = this.current();
        while (ch !== quote &&
            !(interpolate &&
                this.context === Context.StringInterpolationText &&
                this.isInterpolationStart())) {
            if (ch === undefined)
                throw new ParseError(this.index, 'unterminated quote');
            if (ch === '\\') {
                this.advance(); // go past slash
                ch = this.advance(); // escape code
                if (ch === undefined) {
                    throw new ParseError(this.index, 'incomplete escape sequence');
                }
                else if (ch === 'u') {
                    const code = (_a = this.advance(4)) !== null && _a !== void 0 ? _a : '';
                    const match = Lexer.unicodeEscapeRegExp.exec(code);
                    if (!match)
                        throw new ParseError(this.index, 'invalid unicode escape sequence');
                    buf.push(String.fromCharCode(parseInt(code, 16)));
                }
                else if (ch === 'x') {
                    const code = (_b = this.advance(2)) !== null && _b !== void 0 ? _b : '';
                    const match = Lexer.hexadecimalEscapeRegExp.exec(code);
                    if (!match)
                        throw new ParseError(this.index, 'invalid hex escape sequence');
                    buf.push(String.fromCharCode(parseInt(code, 16)));
                }
                else {
                    buf.push(this.unescape(ch));
                }
            }
            else {
                buf.push(ch);
                this.advance();
            }
            ch = this.current();
        }
        if (ch === quote && ch !== '`')
            this.advance(); // advance past ' and "
        this.tokens.push(new ValueToken(buf.join(''), start));
        return true;
    }
    scanToken() {
        if (this.context === Context.Expression ||
            this.context === Context.StringInterpolationExpression ||
            this.context === Context.ObjectLiteral) {
            this.skipWhitespace();
            if (this.isEnd())
                return false;
            if (this.scanHexadecimal())
                return true; // should come before scanNumber
            if (this.scanNumber())
                return true;
            if (this.scanIdentifier())
                return true;
            if (this.scanOperator())
                return true;
            throw new ParseError(this.index);
        }
        else if (this.context === Context.StringInterpolationText) {
            if (this.scanClosingBacktick()) {
                this.contextStack.pop();
                return true;
            }
            if (this.scanInterpolationStart()) {
                this.contextStack.push(Context.StringInterpolationExpression);
                return true;
            }
            if (this.scanString('`', true)) {
                return true;
            }
            throw new ParseError(this.index);
        }
        throw new Error(`invalid context`);
    }
    skipWhitespace() {
        const match = Lexer.whitespaceRegExp.exec(this.input.substring(this.index));
        if (match) {
            const value = match[0];
            this.index += value.length;
        }
    }
    unescape(ch) {
        switch (ch) {
            case 'f':
                return '\f';
            case 'n':
                return '\n';
            case 'r':
                return '\r';
            case 't':
                return '\t';
            case 'v':
                return '\v';
            default:
                break;
        }
        return ch;
    }
}
Lexer.hexadecimalRegExp = /^0[xX][0-9a-fA-F]+/;
Lexer.identifierRegExp = /^[a-zA-Z_$][a-zA-Z0-9_$]*/;
Lexer.interpolationStartRegExp = /^\${/;
Lexer.numberRegExp = /^(?:\d+\.?\d*|\.\d+)(?:[eE]\d+)?/;
Lexer.unicodeEscapeRegExp = /^[a-fA-F0-9]{4}/;
Lexer.hexadecimalEscapeRegExp = /^[a-fA-F0-9]{2}/;
Lexer.whitespaceRegExp = /^\s+/;
export class ValueToken extends BaseToken {
    constructor(value, position) {
        super(value, position, 'value');
    }
}
